import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
// utilities
import { createUserWithRole } from '../../utils/api/user_apis';
import { getOrganizations } from '../../utils/api/organization_apis';
import { getOrgRoles } from '../../utils/api/role_apis';
import { STATUS } from '../../utils/api/sharedfunction';
// components
import ModalWrapper from '../ModalWrapper';
import { FormSchemas, FormWrapper, CommonFooter } from '../FormComponent';
import RoleEnum from '../../enums/RoleEnum';
import { EmailTemplateKeys } from '../../enums/EmailTemplatesEnum';

const CreateUserModal = ({ isOpen, onClose, onError, onCreate }) => {
  const formRef = useRef(null);
  const [userOrgRoles, setUserOrgRoles] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const userRole = useSelector((state) => state.auth.user.roles) || [];
  const isSuperAdmin = userRole.includes(RoleEnum.SUPER_ADMIN.name);
  const isTrialOrgAdmin = userRole.includes(RoleEnum.TRIAL_ORG_ADMIN.name);
  const [organizations, setOrganizations] = useState([]);
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const roleOptions = isTrialOrgAdmin
    ? userOrgRoles.filter((r) => r.name === RoleEnum.TRIAL_USER.name)
    : userOrgRoles;

  useEffect(() => {
    fetchRole();
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isOpen]);

  const fetchRole = async () => {
    if(isOpen){
      const response = await getOrgRoles();
      if (response.status === STATUS.SUCCESS) {
        setUserOrgRoles(response?.data?.roles || []);
      } else {
        console.error('Failed to fetch roles of (organization scope):', response.error);
      }
    }
  };

  const fetchOrganizations = async () => {
    try { 
      const response = await getOrganizations();
      if (response.status === STATUS.SUCCESS) {
        setOrganizations(response.data);
      } else {
        console.error('Failed to fetch organizations:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const handleFormSubmit = async () => {
    if (formRef.current) await formRef.current.submit();
  };
  
  const handleConfirmAdd = async (data) => {
    try {
      setLoading(true);
      const response = await createUserWithRole({
        username: data.username,
        email: data.email,
        roles: [data.role],
        organization: data.organizationId || user.org_id,
      });
      
      if (response.status === STATUS.SUCCESS) {
        onCreate();
        onClose();
      } else {
        onError(response.error || 'Failed to create user', 'error');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      onError(error?.error ?? error?.message ?? 'Failed to create user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onClose,
      disabled: loading,
    },
    {
      label: 'Create',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleFormSubmit,
      disabled: loading,
      loading: loading,
    },
  ];

  const fields = [
    {
      name: 'username',
      type: 'text',
      label: 'Username',
      placeholder: 'Enter username',
      required: true,
    },
    {
      name: 'email',
      type: 'email',
      label: 'Email',
      placeholder: 'Enter email address',
      required: true,
    },
    {
      name: 'role',
      type: 'select',
      label: 'Role',
      required: true,
      options: roleOptions.map((role) => ({
        value: role.id,
        label: role.name,
      })),
    },
    ...(isSuperAdmin && selectedRoleName !== RoleEnum.SUPER_ADMIN.name
      ? [
          {
            name: 'organizationId',
            type: 'select',
            label: 'Organization',
            // placeholder: 'Select an organization',
            required: false,
            options: organizations.map((organization) => ({
              value: organization.id,
              label: organization.name,
            })),
          },
        ]
      : []),
  ];

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Create New User"
      size="small"
      footer={
        <CommonFooter
          buttons={buttons}
          align="right"
        />
      }
      children={
        <FormWrapper
          ref={formRef}
          schema={FormSchemas.userCreation}
          fields={fields}
          onSubmit={handleConfirmAdd}
          defaultValues={{
            username: '',
            email: '',
            role: '',
            organizationId: '',
          }}
        />
      }
    />
  );
};

export default CreateUserModal;
